import { FieldName, FieldNames, Schema } from "../Schema";
import { FieldStructureEntry, StructureAspects, prepareFieldSchema } from "../structure";

/**
 * The CSV surfaces a schema can be projected onto. Each lambda owns one flow, and the
 * flow selects which per-flow overrides apply on top of the shared structure.
 */
export enum CsvFlows {
  /** Equipment CSV export — columns are picked by the user, order follows their choice. */
  EXPORT = "export",
  /** Bulk-change download, whose file is edited and re-uploaded. */
  BULK_CHANGE = "bulkChange",
  /** Import of a bulk-change file — the read half of {@link BULK_CHANGE}. */
  IMPORT = "import",
  /** The create template, downloaded empty and filled in to add new equipment. */
  TEMPLATE = "template",
}

export type CsvFlow = CsvFlows;

/**
 * One column of a CSV structure: the field name, plus optional aspect overrides that
 * apply only within this structure. Position in the {@link CsvStructure} array is the
 * column order — there is no `order` property.
 */
export type CsvFieldStructure = FieldStructureEntry;

/** Ordered list of CSV columns. */
export type CsvStructure = CsvFieldStructure[];

/**
 * Per-flow aspect overrides, applied after the structure entry's own overrides. Use for
 * the few fields whose CSV behaviour differs between flows — e.g. `id` is keyed `_id` in
 * the export index but `id` in the bulk-change one.
 */
export type CsvFlowOverrides = Partial<
  Record<CsvFlows, Record<FieldName, StructureAspects>>
>;

export interface PreparedCsvStructure {
  /**
   * The **whole** schema with the structure's and flow's overrides applied. Row-level
   * operations need every field, not just the columns, so that cross-field getters
   * (`qualificationStatus` reading `controlledEquipmentStatus`, ...) still resolve.
   */
  schema: Schema;
  /** The column field names, in structure order. */
  fields: FieldNames;
}

/**
 * Applies a CSV structure to a schema: walks the structure in order, merging each
 * entry's aspect overrides — then the flow's — over the field's base schema, and
 * returns the full overridden schema alongside the ordered column names.
 *
 * Fields absent from the structure are carried through untouched, so the result stays
 * usable as the full schema for merging and validation.
 *
 * @param structure - Ordered CSV columns. An empty structure yields no columns.
 * @param schema - The base schema to override.
 * @param flow - Which flow's overrides to apply, if any.
 * @param overrides - The per-flow override map.
 * @returns The overridden schema and the ordered column names.
 */
export const prepareCsvStructure = (
  structure: CsvStructure,
  schema: Schema,
  flow?: CsvFlow,
  overrides: CsvFlowOverrides = {}
): PreparedCsvStructure => {
  const preparedSchema: Schema = { ...schema };
  const fields: FieldNames = [];
  const flowOverrides = (flow && overrides[flow]) ?? {};

  for (const entry of structure) {
    // A repeated name would emit the same column twice; the first position wins.
    if (fields.includes(entry.name)) {
      continue;
    }
    preparedSchema[entry.name] = prepareFieldSchema(
      entry.name,
      schema[entry.name],
      entry,
      flowOverrides[entry.name]
    );
    fields.push(entry.name);
  }

  // A flow may also override a field the structure does not list — e.g. switching a
  // column off for the import flow while leaving the shared structure alone.
  for (const [name, override] of Object.entries(flowOverrides)) {
    if (fields.includes(name)) {
      continue;
    }
    preparedSchema[name] = prepareFieldSchema(name, schema[name], override);
  }

  return { schema: preparedSchema, fields };
};
